import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { AgentFileDirectoriesService } from '../services/agent-files-directories.service';
import { AgentFilesMediaService } from '../services/agent-files-media.service';
import {
  AGENT_SYSTEM_DIRECTORY_NAMES,
  AgentFileDirectory,
  AgentFileDirectoryKeys,
} from '../../../../../../ag-common-lib/projects/ag-common-lib/src/lib/models/utils/agent-file-directory.model';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { ChangeSourceType } from '@ag-common-lib/public-api';
import { AgentMedia } from '../../../../../../ag-common-lib/projects/ag-common-lib/src/lib/models/utils/agent-media.model';
import {
  FileDirectoryKeys,
  SystemFileDirectories,
} from '../../../../../../ag-common-lib/projects/ag-common-lib/src/lib/models/utils/file-directory.model';
import { ChangeSourceTypeService } from 'ag-common-svc/lib/services/change-source-type.service';

@Injectable({
  providedIn: 'root',
})
export class AgentImagesService {
  private changeSourceTypeService = inject(ChangeSourceTypeService);

  private _sourceType: ChangeSourceType = this.changeSourceTypeService.sourceType();

  constructor(
    private agentFileDirectoriesService: AgentFileDirectoriesService,
    private agentFilesMediaService: AgentFilesMediaService,
  ) {}

  fetchAgentImages(agentDbId: string): Observable<AgentMedia[]> {
    return this.agentFileDirectoriesService.getList(agentDbId).pipe(
      mergeMap((fileDirectories: AgentFileDirectory[]) => {
        const imagesFileDirectory = fileDirectories.find(
          directory => directory[FileDirectoryKeys.name] === SystemFileDirectories.Images,
        );
        if (!imagesFileDirectory) {
          return [];
        }
        return this.agentFilesMediaService.getList(
          imagesFileDirectory[AgentFileDirectoryKeys.agentDbId],
          imagesFileDirectory[BaseModelKeys.dbId],
        );
      }),
      map(agentMedia => agentMedia as AgentMedia[]),
    );
  }

  // Create agent media in the "Images" directory and return a Promise
  async addAgentMediaToImagesDirectory(
    agentId: string,
    mediaData: AgentMedia,
    action: ChangeSourceType = this._sourceType,
  ): Promise<AgentMedia | void> {
    let fileDirectories = await firstValueFrom(this.agentFileDirectoriesService.getList(agentId));
    Object.assign(mediaData, {
      [BaseModelKeys.actionName]: action,
    });

    const existingDirectories = new Set(fileDirectories.map(dir => dir[FileDirectoryKeys.name]));

    const directoryCreationPromises = AGENT_SYSTEM_DIRECTORY_NAMES.filter(name => !existingDirectories.has(name)).map(
      name => this.createFileDirectories(agentId, name, true),
    );

    if (directoryCreationPromises.length > 0) {
      await Promise.all(directoryCreationPromises);
      fileDirectories = await firstValueFrom(this.agentFileDirectoriesService.getList(agentId));
    }

    const imagesFileDirectory = fileDirectories.find(
      directory => directory[FileDirectoryKeys.name] === SystemFileDirectories.Images,
    );

    if (!imagesFileDirectory) {
      throw new Error('Images directory not found.');
    }

    return this.agentFilesMediaService.create(agentId, imagesFileDirectory[BaseModelKeys.dbId], mediaData);
  }

  getRelativeCropByAspectRatio(imageAspectRatio: number): {
    relativeX1: string;
    relativeY1: string;
    relativeX2: string;
    relativeY2: string;
  } {
    const targetAspectRatio = 6 / 8; // or 0.75
    const height = 1;
    const width = imageAspectRatio * height;

    let cropWidth = width;
    let cropHeight = cropWidth / targetAspectRatio;

    if (cropHeight > height) {
      cropHeight = height;
      cropWidth = cropHeight * targetAspectRatio;
    }

    const left = (width - cropWidth) / 2;
    const top = (height - cropHeight) / 2;

    const relativeX1 = (left / width).toString();
    const relativeY1 = (top / height).toString();
    const relativeX2 = ((left + cropWidth) / width).toString();
    const relativeY2 = ((top + cropHeight) / height).toString();

    return {
      relativeX1,
      relativeY1,
      relativeX2,
      relativeY2,
    };
  }

  createSystemFileDirectories(agentId: string, silent: boolean = false): Promise<boolean[]> {
    const directoryCreationPromises = AGENT_SYSTEM_DIRECTORY_NAMES.map(name =>
      this.createFileDirectories(agentId, name, true, silent),
    );
    return Promise.all(directoryCreationPromises);
  }

  private async createFileDirectories(agentId: string, directoryName: string, isSystem: boolean, silent: boolean = false): Promise<boolean> {
    const directory: AgentFileDirectory = {
      [AgentFileDirectoryKeys.agentDbId]: agentId,
      [FileDirectoryKeys.name]: directoryName,
      [FileDirectoryKeys.isSystem]: isSystem,
    };
    return this.agentFileDirectoriesService
      .create(agentId, Object.assign(directory, { [AgentFileDirectoryKeys.agentDbId]: agentId }) as AgentFileDirectory, silent)
      .then(
        () => true,
        err => {}
      )
      .then(() => false)
      .catch(err => {
        throw err;
      })
      .finally(() => {});
  }
}
